import type { EventEmitter, EventList } from '@onxmaps/map-x/events'
import { WEBMAP_MAP_MANAGER_KEY } from '@/services/map-provider/injection-key'
import { useMapManager } from '@/composables/map-x/use-map-manager'
import { getMapManager } from '@/services/map-provider/map-manager-state'

/**
 * Returns the mapManager.events EventEmitter for subscribing to map events within the Vue context.
 *
 * All map events flow through EventsManager via mapManager.events:
 *   mapEvents.on('map-x-camera-moveend', handler)
 *   mapEvents.on('map-x-source-data', handler)
 *   mapEvents.on('map-x-style-data', handler)
 *   mapEvents.on('map-x-ready', handler)
 *
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns EventEmitter<EventList> — widened from the concrete EventsManager, which adds no public API
 * beyond its base class
 */
export function useMapEvents(
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): EventEmitter<EventList> {
  const mapManager = useMapManager(key)
  return mapManager.events
}

/**
 * Returns the mapManager.events EventEmitter without relying on the Vue injection context.
 *
 * Use this when inject() is not available (e.g., in watchers, lifecycle hooks, utility functions).
 * Returns null if MapManager has not yet been initialized for the given key.
 *
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns EventEmitter<EventList> or null if the MapManager has not yet been initialized
 * for the given key — widened from the concrete EventsManager, which adds no public API
 * beyond its base class
 */
export function getMapEvents(
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): EventEmitter<EventList> | null {
  const mapManager = getMapManager(key)
  if (!mapManager) {
    return null
  }
  return mapManager.events
}
